import { Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { ApiError, ProblemType } from '@/domains/api';
import { useAuth } from '../auth-context';
import { HOME_ROUTE, LOGIN_ROUTE } from '../constants';
import {
  beginSignIn,
  completeSignIn,
  endSessionUrl,
  type IBeginSignIn,
  type ICompletedSignIn
} from '../idp';
import { redirectStorage } from '../storage';
import { AuthShell } from './auth-shell';
import { AuthSplash } from './auth-splash';
import { DoorBackLink } from './door-back-link';
import { IdpUnreachable, useIdpDeparture } from './idp-departure';
import { InvitationRequestForm } from './invitation-request-page';

type Outcome =
  | { kind: 'working' }
  /** The IdP sent `error` back: the sign-in there was cancelled or failed. */
  | { kind: 'cancelled' }
  /** The exchange's empty 401: a token survey-api did not accept, or a disabled account. */
  | { kind: 'refused'; idToken: string }
  /** The exchange's 403 `account-not-linked`, with what the IdP said about who signed in. */
  | { kind: 'not-linked'; email: Nullable<string>; idToken: string }
  /** Anything else; `retry` is what the post-redeem landing had, for the next attempt. */
  | { kind: 'failed'; retry?: IBeginSignIn };

/**
 * `/auth/callback` — where Invent's IdP sends the browser back (the IdP sign-in design,
 * decision 5). Anonymous and outside both zones: one sign-in serves two portals, and the
 * account's kind decides where it lands.
 *
 * Three arrivals:
 * - **`code`** (or `error`) **in the fragment**, where `idp.ts` asks the IdP to put them: the end
 *   of a sign-in this tab started. The code is redeemed at the IdP, its access token exchanged
 *   once for survey-api's session, and the visitor lands where the guard bounced them from, or
 *   at `/`.
 * - **`login_hint` in the query, without a code**: the post-redeem landing — the IdP sends a
 *   person here after they accept an invitation, naming the address and the identity provider.
 *   Sign-in starts again at once with the address, so the IdP does not ask twice, in place of
 *   this history entry: Back must not land on a page that only passes the visitor on. The
 *   provider is not passed on: `idp.ts` names Entra External on every sign-in.
 * - anything else: nothing to finish, so `/login`.
 *
 * **What the exchange refuses is said as survey-api says it**: its empty 401 is a bad token and
 * a disabled account alike, one answer by design, so one screen; its 403 `account-not-linked`
 * names the identity the IdP signed in, since that is whose invitation is missing. Both offer the
 * IdP's end session, for somebody signed in there as somebody else: "Try again" alone would open
 * the same identity. Any other failure — the IdP unreachable, a state this tab did not start,
 * survey-api down — is one "try again".
 *
 * **It runs once per arrival.** The request state the code is checked against is spent by the
 * first attempt, so a second — React's development double effect, a re-render — would fail a
 * sign-in that worked; a ref holds it to one, and its answer is adopted whatever renders after.
 * Rejected: `oidc-client-ts`'s `UserManager.signinCallback` — its stored IdP user and renewals are
 * what decision 1 keeps out.
 */
export function AuthCallbackPage() {
  const { hash, search } = useLocation();
  const navigate = useNavigate();
  const { exchange } = useAuth();
  const [outcome, setOutcome] = React.useState<Outcome>({ kind: 'working' });
  const started = React.useRef(false);

  const answer = new URLSearchParams(hash.slice(1));
  const params = new URLSearchParams(search);
  const finishing = answer.has('code') || answer.has('error');
  const loginHint = params.get('login_hint');
  const arrived = finishing || loginHint !== null;

  React.useEffect(() => {
    if (!arrived || started.current) return;
    started.current = true;

    void (async () => {
      if (!finishing) {
        const retry = { loginHint: loginHint ?? undefined };
        try {
          await beginSignIn({ ...retry, replace: true });
        } catch (idpError) {
          console.warn('[Auth] Could not start the sign-in at the IdP:', idpError);
          setOutcome({ kind: 'failed', retry });
        }
        return;
      }

      let completed: Nullable<ICompletedSignIn> = null;
      try {
        completed = await completeSignIn(window.location.href);
        await exchange(completed.accessToken, completed.idToken);
        navigate(redirectStorage.take() ?? HOME_ROUTE, { replace: true });
      } catch (error) {
        setOutcome(outcomeOf(error, answer.has('error'), completed));
      }
    })();
    // Once per arrival: the URL is read on mount, and `started` holds it there.
  }, []);

  if (!arrived) return <Navigate to={LOGIN_ROUTE} replace />;
  if (outcome.kind === 'working') return <AuthSplash />;
  if (outcome.kind === 'not-linked') {
    return <NotLinked email={outcome.email} idToken={outcome.idToken} />;
  }

  const { title, text } = FAILURES[outcome.kind];
  return (
    <AuthShell title={title}>
      <h1 className="text-2xl text-[var(--color-royal-blue)]">{title}</h1>
      <p className="mt-2 mb-6 text-sm text-muted-foreground">{text}</p>
      <div className="grid gap-3">
        <TryAgain retry={outcome.kind === 'failed' ? outcome.retry : undefined} />
        {outcome.kind === 'refused' && <AnotherIdentity idToken={outcome.idToken} />}
      </div>
      <DoorBackLink isAuth={false} />
    </AuthShell>
  );
}

const FAILURES: Record<'cancelled' | 'refused' | 'failed', { title: string; text: string }> = {
  cancelled: {
    title: 'Sign-in did not finish',
    text: 'Invent’s sign-in sent you back without signing you in.'
  },
  refused: {
    title: 'Could not sign you in',
    text: 'Invent signed you in, but this portal could not open a session for that sign-in.'
  },
  failed: {
    title: 'Sign-in did not finish',
    text: 'Something went wrong while signing you in.'
  }
};

/** Which screen a failed finish is. `completed` is the IdP's answer when the code was redeemed
 *  — what a "not linked" names, and whose id_token both refusals' end session carries. */
function outcomeOf(
  error: unknown,
  idpSaidError: boolean,
  completed: Nullable<ICompletedSignIn>
): Outcome {
  if (idpSaidError) {
    console.warn('[Auth] The IdP sent the sign-in back with an error:', error);
    return { kind: 'cancelled' };
  }
  if (completed && error instanceof ApiError) {
    if (error.status === 401) return { kind: 'refused', idToken: completed.idToken };
    if (error.isProblem(ProblemType.accountNotLinked)) {
      return { kind: 'not-linked', email: completed.email, idToken: completed.idToken };
    }
  }
  console.warn('[Auth] The sign-in could not be finished:', error);
  return { kind: 'failed' };
}

/** A fresh sign-in at the IdP — whatever went wrong, the next attempt starts from the start, with
 *  the address a post-redeem landing named. */
function TryAgain({ retry }: { retry?: IBeginSignIn }) {
  const { leaving, unreachable, leave } = useIdpDeparture();

  return (
    <div className="grid gap-3">
      {unreachable && <IdpUnreachable />}
      <Button
        type="button"
        className="w-full"
        disabled={leaving}
        onClick={() => void leave(() => beginSignIn(retry))}
      >
        {leaving ? 'Opening sign-in…' : 'Try again'}
      </Button>
    </div>
  );
}

/**
 * The exchange's 403 `account-not-linked`: Invent signed somebody in whom no account here signs in
 * as, and no live invitation links. Named by the id_token's `email` — the identity, which may not
 * be the address an account here was made with — and the request for an invitation is filled in
 * with it, for an account at that address that signs in nowhere yet. The way on for somebody
 * signed in at Invent as somebody else is `AnotherIdentity`.
 */
function NotLinked({ email, idToken }: { email: Nullable<string>; idToken: string }) {
  const title = 'Your sign-in is not linked to an account';
  return (
    <AuthShell title={title}>
      <h1 className="text-2xl text-[var(--color-royal-blue)]">{title}</h1>
      <p className="mt-2 mb-6 text-sm text-muted-foreground">
        {email ? (
          <>
            You signed in at Invent as <strong className="break-words">{email}</strong>.
          </>
        ) : (
          'You signed in at Invent.'
        )}{' '}
        No Veritant account signs in with that identity, and no invitation links one.
      </p>
      <InvitationRequestForm email={email ?? ''} />
      <div className="mt-6 grid gap-3 border-t border-border pt-6">
        <AnotherIdentity idToken={idToken} />
      </div>
      <DoorBackLink isAuth={false} />
    </AuthShell>
  );
}

/** The IdP's end session with the id_token just redeemed, which the next sign-in follows with a
 *  fresh choice of identity. */
function AnotherIdentity({ idToken }: { idToken: string }) {
  const { leaving, unreachable, leave } = useIdpDeparture();
  const signOutThere = () =>
    leave(async () => window.location.assign(await endSessionUrl(idToken)));

  return (
    <>
      {unreachable && <IdpUnreachable />}
      <Button
        type="button"
        variant="outline"
        className="w-full"
        disabled={leaving}
        onClick={() => void signOutThere()}
      >
        {leaving ? 'Opening Invent…' : 'Sign in with another identity'}
      </Button>
    </>
  );
}
